import React, { useState, useEffect } from 'react';
import { planEncryptionKey, encryptByDES, decrypt, getDbDateFormat, formatDate } from './commonHelpers';
import { v4 as uuidv4 } from "uuid";

export default function PlanDetailsManager() {
    const [formData, setFormData] = useState({
        planId: '',
        customerId: '',
        checkoutPageUrl: '',
        startDate: '',
        endDate: ''
    });

    const [planDetails, setPlanDetails] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isViewerMode, setIsViewerMode] = useState(false);

    useEffect(() => {
        // Check if we're in viewer mode (URL has key parameter)
        const urlParams = new URLSearchParams(window.location.search);
        const encryptedKey = urlParams.get('key');

        if (encryptedKey) {
            setIsViewerMode(true);
            setLoading(true);
            
            try {
                // Decrypt the data
                const decryptedData = decrypt(encryptedKey.replace(/ /g, '+').replace(/-/g, "+").replace(/_/g, "/").replace(/,/g, "="), planEncryptionKey);
                const parsedData = JSON.parse(decryptedData);

                setPlanDetails(parsedData);
                setLoading(false);
            } catch (err) {
                setError('Failed to decrypt or parse the plan details');
                setLoading(false);
                console.error('Decryption error:', err);
            }
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Create a copy of form data to modify
        let submissionData = { ...formData };
        
        // If end date is not provided, set it to start date + 30 days
        if (!submissionData.endDate && submissionData.startDate) {
            const startDate = new Date(submissionData.startDate);
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 30);
            
            // Format the date as YYYY-MM-DD
            const formattedEndDate = endDate.toISOString().split('T')[0];
            submissionData.endDate = formattedEndDate;
        }
        
        // Create the data object with all inputs
        const dataObject = {
            PlanID: parseInt(submissionData.planId),
            ContactID: parseInt(submissionData.customerId),
            checkoutPageUrl: submissionData.checkoutPageUrl,
            StartDate: formatDate(submissionData.startDate,getDbDateFormat()),
            EndDate: formatDate(submissionData.endDate,getDbDateFormat()),
            TransactionID: uuidv4(),
        };
        
        // Encrypt the data object
        const jsonString = JSON.stringify(dataObject);
        const encryptedData = encryptByDES(jsonString, planEncryptionKey);
        
        // Generate URL with encrypted data as query parameter
        const baseUrl = submissionData.checkoutPageUrl;
        const separator = baseUrl.includes('?') ? '&' : '?';
        const redirectUrl = `${baseUrl}${separator}key=${encodeURIComponent(encryptedData)}`;
        
        console.log('Form submitted:', submissionData);
        console.log('Data object:', dataObject);
        console.log('Encrypted data:', encryptedData);
        console.log('Redirect URL:', redirectUrl);
        
        // Open the generated URL in a new tab
        window.open(redirectUrl, '_blank');
    };

    // Viewer Mode - Display decrypted details
    if (isViewerMode) {
        if (loading) {
            return (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    fontSize: '18px',
                    color: '#666'
                }}>
                    Loading Transaction details...
                </div>
            );
        }

        if (error) {
            return (
                <div style={{ 
                    padding: '20px', 
                    maxWidth: '600px', 
                    margin: '0 auto',
                    textAlign: 'center'
                }}>
                    <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>
                        Error Loading Plan Details
                    </h1>
                    <p style={{ color: '#666', fontSize: '16px' }}>
                        {error}
                    </p>
                    <button 
                        onClick={() => window.close()}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Close
                    </button>
                </div>
            );
        }

        return (
            <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                <h1 style={{ 
                    textAlign: 'center', 
                    marginBottom: '30px', 
                    color: '#333',
                    borderBottom: '2px solid #007bff',
                    paddingBottom: '10px'
                }}>
                    Transaction Details
                </h1>
                
                <div style={{ 
                    backgroundColor: '#f9f9f9', 
                    padding: '20px', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                                    <div style={{ display: 'grid', gap: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
                        <strong style={{ color: '#333' }}>Transaction ID:</strong>
                        <span style={{ color: '#007bff', fontWeight: 'bold' }}>{planDetails?.TransactionID || 'N/A'}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
                        <strong style={{ color: '#333' }}>Order ID:</strong>
                        <span style={{ color: '#007bff', fontWeight: 'bold' }}>{planDetails?.OrderID || 'N/A'}</span>
                    </div>
                    {planDetails?.ErrorMessage && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
                        <strong style={{ color: '#333' }}>Error Message:</strong>
                        <span style={{ color: '#007bff', fontWeight: 'bold' }}>{planDetails?.ErrorMessage || 'N/A'}</span>
                    </div>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
                        <strong style={{ color: '#333' }}>Order Status:</strong>
                        <span style={{ 
                            color: planDetails?.OrderStatus === 'completed' ? '#28a745' : 
                                   planDetails?.OrderStatus === 'pending' ? '#ffc107' : 
                                   planDetails?.OrderStatus === 'failed' ? '#dc3545' : '#007bff',
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                        }}>
                            {planDetails?.OrderStatus || 'N/A'}
                        </span>
                    </div>
                </div>
                    
                    <div style={{ marginTop: '30px', textAlign: 'center' }}>
                        <button 
                            onClick={() => window.close()}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Generator Mode - Display form
    return (
        <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
            <h1 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
                Encrypted URL Generator
            </h1>
            
            <form onSubmit={handleSubmit} style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="planId" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Plan ID:
                    </label>
                    <input
                        type="number"
                        id="planId"
                        name="planId"
                        value={formData.planId}
                        onChange={handleInputChange}
                        required
                        min="1"
                        step="1"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                        placeholder="Enter Plan ID (integer)"
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="customerId" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Customer ID:
                    </label>
                    <input
                        type="number"
                        id="customerId"
                        name="customerId"
                        value={formData.customerId}
                        onChange={handleInputChange}
                        required
                        min="0"
                        max="999999"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                        placeholder="Enter Customer ID (0-6 digits)"
                        title="Please enter 0 to 6 digits"
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="checkoutPageUrl" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Checkout Page URL:
                    </label>
                    <input
                        type="url"
                        id="checkoutPageUrl"
                        name="checkoutPageUrl"
                        value={formData.checkoutPageUrl}
                        onChange={handleInputChange}
                        required
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                        placeholder="https://example.com/checkout"
                    />
                </div>



                <div style={{ marginBottom: '20px', display: 'flex', gap: '15px' }}>
                    <div style={{ flex: '1' }}>
                        <label htmlFor="startDate" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Start Date:
                        </label>
                        <input
                            type="date"
                            id="startDate"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            required
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    <div style={{ flex: '1' }}>
                        <label htmlFor="endDate" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            End Date:
                        </label>
                        <input
                            type="date"
                            id="endDate"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '16px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                >
                    Generate Encrypted URL
                </button>
            </form>
        </div>
    );
} 